import React, { useState, useMemo, useContext, useEffect, useRef } from "react";
import axios from "axios";
import { useHiringRequests } from "../../contexts/HiringRequestsContext";
import { useCampaigns } from "../../contexts/CampaignsContext";
import Modal from "../../components/UI/Modal";
import HiringRequestForm from "../../components/Form/HiringRequestForm";
import CampaignForm from "../../components/Form/CampaignForm";
import ReusableTable from "../../components/UI/ReusableTable";
import "react-loading-skeleton/dist/skeleton.css";
import { FaExchangeAlt } from "react-icons/fa";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import ReactPaginate from 'react-paginate';
import Drawer from "../../components/UI/Drawer";
import HiringRequestDetails from "./RequestDetails";
import { AuthContext } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { MoreVertical, AlertTriangle } from 'lucide-react';
import { createPortal } from 'react-dom';

// Custom Alert Dialog Component
const AlertDialog = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Delete", cancelText = "Cancel" }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-end md:items-center z-50 backdrop-blur-sm">
      <div className="bg-white dark:bg-black p-6 rounded-t-lg md:rounded-lg shadow-lg w-full max-w-sm mx-auto transition-transform duration-300">
        <h2 className="text-lg font-semibold text-center dark:text-white">Are you sure?</h2>
        <p className="text-gray-600 mt-2 text-center dark:text-gray-500">This action cannot be undone.</p>
        <div className="mt-4 flex justify-between space-x-3">
          <button
            className="block w-full py-2 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-10 py-2 text-white bg-red-600 rounded hover:bg-red-700 w-full"
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const HiringRequests = React.memo(() => {
  const { user, roles } = useContext(AuthContext);
  const isManager = roles.includes("Manager");
  const navigate = useNavigate();
  const { requests, fetchHiringRequests, deleteHiringRequest, pagination, loading } = useHiringRequests(); 
  const { addCampaign } = useCampaigns();
  const [campaignError, setCampaignError] = useState(null); 
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedHiringRequest, setSelectedHiringRequest] = useState(null);
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [selectedRequestForCampaign, setSelectedRequestForCampaign] = useState(null);
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isRevertModalOpen, setIsRevertModalOpen] = useState(false);
  const [revertFeedback, setRevertFeedback] = useState("");
  const [revertRequestId, setRevertRequestId] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [dropdownCoords, setDropdownCoords] = useState({ x: 0, y: 0 });
  const buttonRefs = useRef({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const openDrawerForDetails = (hiringRequest) => {
    setSelectedHiringRequest(hiringRequest);
    setIsDrawerOpen(true);
  };
  
  const handleDeleteHiringRequest = async (id) => {
    setDeleteId(id);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteHiringRequest(deleteId);
      setNotification({message: "Hiring request deleted successfully", type: "success"});
    } catch (error) {
      setNotification({message: "Failed to delete hiring request", type: "error"});
    } finally {
      setShowDeleteDialog(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteId(null);
  };

  const revertHiringRequest = async (id, feedback) => {
    try {
      const token = localStorage.getItem('authToken');
      const axiosInstance = axios.create({
        baseURL: 'https://hrmsnew.prepstripe.com/api/',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const response = await axiosInstance.post(`recruitment-requests/${id}/revert`, { feedback });
      if (response?.status === 200 || response?.status === 201) {
        setNotification({
          message: response?.data?.message || 'Hiring request reverted successfully.',
          type: 'success'
        });
        fetchHiringRequests();
      } else {
        setNotification({
          message: response?.data?.message || 'Failed to revert hiring request.',
          type: 'error'
        });
      }
    } catch (error) {
      setNotification({
        message: error?.response?.data?.message || 'Failed to revert hiring request.',
        type: 'error'
      });
    }
  };

  const openRevertModal = (id) => {
    setRevertRequestId(id);
    setRevertFeedback("");
    setIsRevertModalOpen(true);
  };
  const closeRevertModal = () => {
    setIsRevertModalOpen(false);
    setRevertFeedback("");
    setRevertRequestId(null);
  };
  const handleRevertSubmit = async () => {
    if (revertFeedback.trim()) {
      await revertHiringRequest(revertRequestId, revertFeedback.trim());
      closeRevertModal();
    } else {
      setNotification({ message: "Feedback is required to revert.", type: "error" });
    }
  };

  const handleClickOutside = (event) => {
    if (!event.target.closest('.dropdown-container')) {
      setOpenDropdownId(null);
    }
  };

  const handleDropdownToggle = (requestId, event) => {
    event.stopPropagation();
    if (openDropdownId === requestId) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(requestId);
      // Calculate portal coordinates
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      const dropdownWidth = 200;
      const dropdownHeight = 180;
      let x = rect.right - dropdownWidth;
      let y = rect.bottom + 4;
      if (x < 0) x = rect.left - dropdownWidth;
      if (y + dropdownHeight > window.innerHeight) {
        y = rect.top - dropdownHeight - 4;
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
      if (x < 0) x = 8;
      if (y < 0) y = 8;
      setDropdownCoords({ x, y });
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Memoized Columns Definition
  const columns = useMemo(
    () => [
      { label: "Requester", field: "requester_name", type: "text" },
      { label: "Designation", field: "designation", type: "text" },
      { label: "Department", field: "department", type: "text" },
      { label: "Hiring Type", field: "hiring_type", type: "text" },
      { label: "No. of Positions", field: "number_of_positions", type: "number" },
      { label: "Location", field: "location", type: "text" },
      { label: "Employment Type", field: "employment_type", type: "text" },
      { label: "Recruitment Status", field: "recruitment_request_status", type: "text" },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <div className="flex items-center justify-center">
            <div className="relative dropdown-container">
              <button
                ref={el => { if (el) buttonRefs.current[row.id] = el; }}
                onClick={e => handleDropdownToggle(row.id, e)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
            {openDropdownId === row.id && createPortal(
              <div
                style={{
                  position: 'fixed',
                  top: dropdownCoords.y,
                  left: dropdownCoords.x,
                  zIndex: 9999,
                }}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl min-w-[200px]"
              >
                <div className="py-1 flex flex-col space-y-0">
                  {/* View Details */}
                  <button
                    onClick={() => {
                      openDrawerForDetails(row);
                      setOpenDropdownId(null);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors block"
                  >
                    View Details
                  </button>
                  {/* Edit */}
                  {isManager && user?.id === row.requester_id && row.recruitment_request_status === "Pending" && (
                    <button
                      onClick={() => {
                        openModalForEdit(row);
                        setOpenDropdownId(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors block"
                    >
                      Edit
                    </button>
                  )}
                  {/* Delete */}
                  {isManager && row.status !== "Approved" && user?.id === row.requester_id && row.recruitment_request_status === "Pending" && (
                    <button className="text-left text-red-600 text-sm hover:text-red-800 px-3 py-2" onClick={() => {
                      handleDeleteHiringRequest(row.id);
                      setOpenDropdownId(null);
                    }}>
                      Delete
                    </button>
                  )}
                  {/* Revert */}
                  {(roles.includes("HR")) && ["Approved", "Reverted"].includes(row.recruitment_request_status) && (
                    <button
                      onClick={() => {
                        openRevertModal(row.id);
                        setOpenDropdownId(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors block"
                    >
                      Revert
                    </button>
                  )}
                  {row.recruitment_request_status === "Approved" && roles.includes("HR") ? (
                  <button
                    onClick={() => openCampaignModal(row)}
                    className="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors block"
                    title="Convert to Campaign"
                  >
                    Convert to Campaign
                  </button>
                  ) : null}
                </div>
              </div>,
              document.body
            )}
          </div>
        ),
      },
    ],
    [roles, isManager, user, openDropdownId, dropdownCoords]
  );

  const openModalForAdd = () => {
    setEditingRequest(null);
    setIsModalOpen(true);
  };

  const openModalForEdit = (request) => {
    setEditingRequest(request);
    setIsModalOpen(true);
  };

  const openCampaignModal = (request) => {
    const today = new Date();
    const formattedToday = today.toISOString().split("T")[0];
    const campaignData = {
      ...request,
      id: request.id,
      to_date: request.deadline,
      from_date: formattedToday,
      starting_salary_from: "",
      starting_salary_to: "",
      ctc: request.stipend_ctc_per_month || "", // Prefill CTC with stipend data
    };
    setSelectedRequestForCampaign(campaignData);
    setIsCampaignModalOpen(true);
  };

  const handleCampaignSubmit = async (campaignData) => {
    try {
      // Create axios instance with proper authorization
      const token = localStorage.getItem('authToken');
      const axiosInstance = axios.create({
        baseURL: 'https://hrmsnew.prepstripe.com/api/',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      
      const response = await axiosInstance.post('recruitment-requests/convert-to-campaign', {
        id: selectedRequestForCampaign.id,
        ...campaignData  // Send all campaign form data
      });
      
      if (response?.status === 200 || response?.status === 201) {
        setNotification({
          message: "Hiring request converted to campaign successfully.",
          type: "success"
        });
        setIsCampaignModalOpen(false);
        setSelectedRequestForCampaign(null);
        setCampaignError(null);
        // Refresh the hiring requests list to reflect the changes
        fetchHiringRequests();
        
        // Redirect to the newly created campaign details
        const campaignId = response?.data?.data?.id || response?.data?.id;
        if (campaignId) {
          navigate(`${getRoleBasedRoute("/admin/campaigns", roles)}?campaign_id=${campaignId}`);
        }
      } else {
        setNotification({
          message: `${response?.data?.message || "Unknown error occurred while converting to campaign."}`,
          type: "error"
        });
      }
    } catch (error) {
      // console.log("Error converting to campaign:", error);
      setNotification({
        message: error?.response?.data?.message || "Failed to convert hiring request to campaign.",
        type: "error"
      });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRequest(null);
  };

  const closeCampaignModal = () => {
    setIsCampaignModalOpen(false);
    setSelectedRequestForCampaign(null);
  };

  // Memoized Exclude Filter Columns
  const excludeFilterColumns = useMemo(() => ["actions", "job_description", "justification"], []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchHiringRequests(newPage);
    }
  };
  
  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-950 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">Hiring Requests</h2>
        {isManager && (
          <button
            onClick={openModalForAdd}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto">
            Add Hiring Request
          </button>
        )}
      </div>

      {/* data table */}
      <div>
        <ReusableTable
          data={requests}
          loading={loading}
          columns={columns}
          filterable={true}
          excludeFilterColumns={excludeFilterColumns}
          onFilterChange={(filtering) => setIsFiltering(filtering)}
        />

        {/* Pagination */}
        {!isFiltering && requests?.length > 0 && (
          <ReactPaginate
            breakLabel="..."
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            pageCount={pagination.totalPages}
            renderOnZeroPageCount={null}
            nextLabel="Next >"
            previousLabel="< Prev"
            previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
            nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
            containerClassName="flex items-center justify-center mt-4"
            pageClassName="mx-1 px-3 py-1 border rounded dark:text-white"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700"
          />
        )}
      </div>

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingRequest ? "Edit Hiring Request" : "Add New Hiring Request"}
        fullSize={false}>
        <HiringRequestForm
          closeModal={closeModal}
          request={editingRequest}
          isEditing={Boolean(editingRequest)}
          setNotification={setNotification}
        />
      </Modal>

      {/* Modal for Campaign Conversion */}
      <Modal
        isOpen={isCampaignModalOpen}
        onRequestClose={closeCampaignModal}
        title="Convert to Campaign"
        fullSize={false}>
        {selectedRequestForCampaign && (
          <CampaignForm
            closeModal={closeCampaignModal}
            onSubmit={handleCampaignSubmit}
            initialValues={selectedRequestForCampaign}
            mode="Convert"
          />
        )}
      </Modal>

      {/* Modal for Reverting Hiring Request */}
      <Modal
        isOpen={isRevertModalOpen}
        onRequestClose={closeRevertModal}
        title="Revert Hiring Request"
        fullSize={false}
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Feedback <span className="text-red-500">*</span></label>
          <textarea
            value={revertFeedback}
            onChange={e => setRevertFeedback(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Please provide feedback for reverting this request..."
            required
          />
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={closeRevertModal}
              className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRevertSubmit}
              className="px-4 py-2 text-white bg-orange-600 rounded-md hover:bg-orange-700"
            >
              Revert
            </button>
          </div>
        </div>
      </Modal>

      {/* Drawer for Hiring request Details */}
      <Drawer
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          border_radius="md:rounded-none"
          showClose={false}
          ChilDiv=""
          ChildParent="">
          <HiringRequestDetails
            hiringRequest={selectedHiringRequest}
            hiring_request_id={selectedHiringRequest?.id}
            close={closeDrawer}
        />
      </Drawer>

      {/* Custom Alert Dialog */}
      <AlertDialog
        isOpen={showDeleteDialog}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Hiring Request"
        message="Are you sure you want to delete this hiring request? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
});

export default HiringRequests;
